import { LeftRight } from "@/components/LeftRight.tsx";
import { WSelect, type ConfiguredWSelectProps } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { ResourceIcon } from "@/components/icon/ResourceIcon.tsx";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { UnattachedItem } from "@/modules/disks/UnattachedItem.tsx";
import { getResourceStringId } from "@/modules/disks/disk/resourceId.ts";
import { diskAttachableQueryAtom } from "@/modules/disks/disk/resourceQuery.ts";
import { UNASSIGNED_RESOURCE, getDisplayName } from "@/modules/network/ipAddress/resourceId.ts";
import { hostPoolQueryAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";

export interface AttachedToSelectCustomProps extends ConfiguredWSelectProps<
    VirtualMachineLoc | typeof UNASSIGNED_RESOURCE,
    string
> {}

function AttachedToSelectCustom(props: AttachedToSelectCustomProps) {
    return (
        <WSelect
            aria-label="Select resource to assign to"
            getKey={getResourceStringId}
            getTextValue={getDisplayName}
            searchItems
            viewer={(item) =>
                item !== UNASSIGNED_RESOURCE && item !== undefined ? (
                    <LeftRight
                        icon={<ResourceIcon item={item} size="size-1.1em mb-[-0.25em]" />}
                        title={getDisplayName(item)}
                    />
                ) : (
                    <span className="text-muted">
                        <Trans>Detached</Trans>
                        {/* getDisplayName(item ?? null) */}
                    </span>
                )
            }
            renderUnknown={() => (
                <span className="text-error">
                    <Trans>Unknown resource</Trans>
                </span>
            )}
            {...props}
        >
            {(item) =>
                item !== UNASSIGNED_RESOURCE ? (
                    <LeftRight icon={<ResourceIcon item={item} />} title={getDisplayName(item)} />
                ) : (
                    <UnattachedItem />
                )
            }
        </WSelect>
    );
}

export interface AttachedToSelectProps extends Omit<AttachedToSelectCustomProps, "items"> {
    location: string;
    storagePoolUuid: string | undefined;
}

function AttachedToSelectLoader({ location, storagePoolUuid, ...props }: AttachedToSelectProps) {
    const items = useSuspenseQueryAtom(diskAttachableQueryAtom(location));
    const poolsMap = useSuspenseQueryAtom(hostPoolQueryAtom(location));
    const poolUuids = [...poolsMap.values()]
        .filter((item) => item.storage_pool_uuid === storagePoolUuid)
        .map((item) => item.uuid);
    const poolVms = items.filter((item) => item.designated_pool_uuid && poolUuids.includes(item.designated_pool_uuid));

    return <AttachedToSelectCustom items={[UNASSIGNED_RESOURCE, ...poolVms]} {...props} />;
}

export function AttachedToSelect(props: AttachedToSelectProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <AttachedToSelectCustom {...props} {...renderProps} />}>
            <AttachedToSelectLoader {...props} />
        </WSelectWrapper>
    );
}
